<template lang="pug">
  ProgramSettingsFormWrapper#BadgeSettings(
      :form-data="formData"
      :keys-to-reset="keysToReset"
      :disable-save.sync="disableSave"
      title="Badge Settings"
    )

    WtTile(parent vertical :col="12")
      WtTile(child :col="12")
        .columns.is-multiline.is-variable
          .column.is-3
            WtSwitchQuestionInput(
              label="Enable Badges"
              :tooltip-text="enableBadgesTooltip"
              v-model="badges"
              name="enable badges"
              @input="disableSave = false"
            )
          .column.is-9(v-if="badges")
            p Badges can be earned from {{ this.date(this.$auth.user().program.badgesOnDate, "DATE_FORMAT_FULL_MONTH").formatted }} and onward.

        .columns.is-multiline.is-variable(v-if="badges")
          .column
            WtSwitchQuestionInput(
              label="Activity Badges"
              v-model="activityBadges"
              name="enable activity badges"
              @input="disableSave = false"
            )
            BCollapse(
              aria-id="expandActivityBadges"
              :open.sync="expandActivityBadges"
            )
              .header(
                slot='trigger',
                role='button',
                aria-controls='expandActivityBadges'
                :class="{ 'question-expanded': expandActivityBadges}"
              )
                span View Activity Badges
                BIcon(:icon="expandActivityBadges ? 'chevron-up' : 'chevron-down'")
              .content
                .card(v-for="badge in activityBadgeArray")
                  .card-content
                    .columns
                      .column.is-2
                        BImage(:src="badge.imageUrl" :alt="badge.name" class="image is-32x32")
                      .column.is-10
                        strong {{ badge.name }}
                        br
                        small {{ badge.description }}

          .column
            WtSwitchQuestionInput(
              label="Social Badges"
              v-model="socialBadges"
              name="enable social badges"
              @input="disableSave = false"
            )
            BCollapse(
              aria-id="expandSocialBadges"
              :open.sync="expandSocialBadges"
            )
              .header(
                slot='trigger',
                role='button',
                aria-controls='expandSocialBadges'
                :class="{ 'question-expanded': expandSocialBadges}"
              )
                span View Social Badges
                BIcon(:icon="expandSocialBadges ? 'chevron-up' : 'chevron-down'")
              .content
                .card(v-for="badge in socialBadgeArray")
                  .card-content
                    .columns
                      .column.is-2
                        BImage(:src="badge.imageUrl" :alt="badge.name" class="image is-32x32")
                      .column.is-10
                        strong {{ badge.name }}
                        br
                        small {{ badge.description }}

          .column(v-if="$auth.user().program.isWellnessMetricsOn")
            WtSwitchQuestionInput(
              label="Wellness Badges"
              v-model="wellnessBadges"
              name="enable wellness badges"
              @input="disableSave = false"
            )
            BCollapse(
              aria-id="expandWellnessBadges"
              :open.sync="expandWellnessBadges"
            )
              .header(
                slot='trigger',
                role='button',
                aria-controls='expandWellnessBadges'
                :class="{ 'question-expanded': expandWellnessBadges}"
              )
                span View Wellness Badges
                BIcon(:icon="expandWellnessBadges ? 'chevron-up' : 'chevron-down'")
              .content
                .card(v-for="badge in wellnessBadgeArray")
                  .card-content
                    .columns
                      .column.is-2
                        BImage(:src="badge.imageUrl" :alt="badge.name" class="image is-32x32")
                      .column.is-10
                        strong {{ badge.name }}
                        br
                        small {{ badge.description }}
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTooltip from "@@/WtTooltip/WtTooltip";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";

export default {
  name: "BadgeSettings",

  components: {
    WtTile,
    WtTooltip,
    WtButton,
    WtTitle,
    WtSwitchQuestionInput,
    ProgramSettingsFormWrapper
  },

  mixins: [dateUtilityMixin],

  data() {
    return {
      badgesToggle: false,
      disableSave: true,
      expandActivityBadges: false,
      expandSocialBadges: false,
      expandWellnessBadges: false,
      activityBadgeArray: [],
      activityBadgesToggle: false,
      socialBadgeArray: [],
      socialBadgesToggle: false,
      wellnessBadgeArray: [],
      wellnessBadgesToggle: false
    };
  },

  computed: {
    ...mapGetters("program", ["programSettingsCopy", "isCoreTier"]),

    enableBadgesTooltip() {
      return `Enable badges on your program`;
    },

    formData() {
      return {
        badges: Number(this.badges),
        activityBadges: Number(this.activityBadges),
        socialBadges: Number(this.socialBadges),
        wellnessBadges: Number(this.wellnessBadges)
      };
    },

    badges: {
      set(badges) {
        this.updateProgramSettingsCopy({
          key: "badges",
          value: badges
        });
      },
      get() {
        return Number(this.programSettingsCopy.badges);
      }
    },

    activityBadges: {
      set(activityBadges) {
        this.updateProgramSettingsCopy({
          key: "activityBadgesOn",
          value: activityBadges ? 1 : 0
        });
      },
      get() {
        return Number(this.programSettingsCopy.activityBadgesOn);
      }
    },

    socialBadges: {
      set(socialBadges) {
        this.updateProgramSettingsCopy({
          key: "socialBadgesOn",
          value: socialBadges ? 1 : 0
        });
      },
      get() {
        return Number(this.programSettingsCopy.socialBadgesOn);
      }
    },

    wellnessBadges: {
      set(wellnessBadges) {
        this.updateProgramSettingsCopy({
          key: "wellnessBadgesOn",
          value: wellnessBadges ? 1 : 0
        });
      },
      get() {
        return Number(this.programSettingsCopy.wellnessBadgesOn);
      }
    },

    keysToReset() {
      return ["badges", "activityBadges", "socialBadges", "wellnessBadges"];
    }
  },

  async mounted() {
    [
      this.activityBadgeArray,
      this.socialBadgeArray,
      this.wellnessBadgeArray
    ] = await Promise.all([
      this.fetchBadges("activity"),
      this.fetchBadges("social"),
      this.fetchBadges("wellness")
    ]);
  },

  methods: {
    ...mapActions("program", ["updateProgramSettingsCopy"]),
    ...mapActions("members", ["fetchBadges"])
  }
};
</script>

<style lang="sass">
#badge-settings
  margin-right: 1rem
  margin-top: 1rem

.content figure
  margin-left: 0px
</style>
